import React from 'react';

import {
  BoltRounded, CalculateRounded, FunctionsRounded, ScienceRounded,
} from '@mui/icons-material';

import { ColorHex } from '../../components/ProfileIcon/types';
import {
  BaseColorKeys, PureColorKeys, SubjectColorKeys,
} from '../../theme';
import {
  Grade,
  Location,
  StudentState,
  Subject,
  UnenrolmentReason,
  UnenrolmentType,
} from '../../types/common';

export const PERMISSION_GROUP_PREFIX = 'CPG';
export const CLASS_PERMISSION_GROUP_PREFIX = 'CL';

export const DEFAULT_LOCATION = Location.Chatswood;

export interface SubjectDetails {
  key: string,
  colorKey: SubjectColorKeys,
  label: string,
  value: string,
  icon: React.ReactElement | React.ReactElement[],
}

export interface StudentStateDetails {
  key: string,
  colorKey: PureColorKeys | BaseColorKeys,
  label: string,
  value: string,
}

export const DEFAULT_PROFILE_COLOR: ColorHex = '#0c374d';
export const PROFILE_COLOR_OPTIONS: ColorHex[] = [
  '#0c374d', '#1287a8', '#667541', '#a06681', '#6b3975', '#cd594a',
];

// Multiple Course Discounts are:
// 2 Courses: 5%
// 3 Courses: 8%
// 4 Courses: 10%
export const DISCOUNT_MULTIPLE_COURSE: {
  [courseCount: number]: number;
} = {
  1: 0,
  2: 5,
  3: 8,
  4: 10,
};

export const ADAPTER_DAY_JS_LOCALE = 'en-au';

export const ENROLLABLE_STUDENT_STATES = [
  StudentState.Lead,
  StudentState.Enrolled,
  StudentState.PendingAttendance,
  StudentState.Active,
  StudentState.OnTrial,
];

export const STUDENT_STATES: { [key: string]: StudentStateDetails } = {
  [StudentState.Lead]: {
    key: StudentState.Lead,
    colorKey: 'lightRed',
    value: StudentState.Lead,
    label: 'Lead',
  },
  [StudentState.Enrolled]: {
    key: StudentState.Enrolled,
    colorKey: 'lightOrange',
    value: StudentState.Enrolled,
    label: 'Enrolled',
  },
  [StudentState.PendingAttendance]: {
    key: StudentState.PendingAttendance,
    colorKey: 'lightYellow',
    value: StudentState.PendingAttendance,
    label: 'Pending Attendance',
  },
  [StudentState.OnTrial]: {
    key: StudentState.OnTrial,
    colorKey: 'lightYellow',
    value: StudentState.OnTrial,
    label: 'On Trial',
  },
  [StudentState.Active]: {
    key: StudentState.Active,
    colorKey: 'lightGreen',
    value: StudentState.Active,
    label: 'Active',
  },
  [StudentState.Graduated]: {
    key: StudentState.Graduated,
    colorKey: 'lightBlue',
    value: StudentState.Graduated,
    label: 'Graduated',
  },
  [StudentState.Archived]: {
    key: StudentState.Archived,
    colorKey: 'lightBlue',
    value: StudentState.Archived,
    label: 'Archived',
  },
};

export const SUBJECTS: { [key: string]: SubjectDetails } = {
  chem: {
    key: Subject.Chemistry,
    value: Subject.Chemistry,
    label: 'Chemistry',
    colorKey: 'chemistry',
    icon: <ScienceRounded />,
  },
  phys: {
    key: Subject.Physics,
    value: Subject.Physics,
    label: 'Physics',
    colorKey: 'physics',
    icon: <BoltRounded />,
  },
  // Subject.MathsAdv: {
  //   key: Subject.MathsAdv
  //   value: Subject.MathsAdv,
  //   label: 'Maths Adv',
  //   colorKey: 'mathsAdv',
  //   icon: <CalculateRounded />,
  // },
  mtx1: {
    key: Subject.MathsExt1,
    value: Subject.MathsExt1,
    label: 'Maths Ext 1',
    colorKey: 'mathsExt1',
    icon: <CalculateRounded />,
  },
  mtx2: {
    key: Subject.MathsExt2,
    value: Subject.MathsExt2,
    label: 'Maths Ext 2',
    colorKey: 'mathsExt2',
    icon: <FunctionsRounded />,
  },
};

export const GRADES = [{
  key: Grade.HSC,
  value: Grade.HSC,
  label: 'HSC',
}, {
  key: Grade.Prelim,
  value: Grade.Prelim,
  label: 'Prelim',
}];

export const ANALYTICS_QUESTION_TYPES = [
  'calculationAnalytics',
  'extendedResponseAnalytics',
  'graphingAnalytics',
  'multipleChoiceAnalytics',
  'proofAnalytics',
  'shortAnswerAnalytics',
];

export const MISTAKE_TYPES = {
  missedMarks: 'Missed Marks',
  inaccuracyMarks: 'Inaccuracies',
  sillyMarks: 'Silly Mistakes',
  correctMarks: 'Correct',
};

export const MAX_CLASS_SIZE = 9;
export const DESIRED_LESSON_SIZE = 9;

export const UNENROLMENT_TYPES = [
  {
    key: UnenrolmentType.Quit,
    value: UnenrolmentType.Quit,
    label: 'Quit',
  },
  {
    key: UnenrolmentType.Move,
    value: UnenrolmentType.Move,
    label: 'Move',
  },
];

const UNENROLMENT_SHARED_REASONS = [
  {
    key: UnenrolmentReason.TeacherDissatisfaction,
    value: UnenrolmentReason.TeacherDissatisfaction,
    label: 'Teacher dissatisfaction',
  },
  {
    key: UnenrolmentReason.MismatchingSchedule,
    value: UnenrolmentReason.MismatchingSchedule,
    label: 'Mismatching schedule',
  },
  {
    key: UnenrolmentReason.ScheduleConflict,
    value: UnenrolmentReason.ScheduleConflict,
    label: 'Schedule conflict',
  },
  {
    key: UnenrolmentReason.UnaffordableFees,
    value: UnenrolmentReason.UnaffordableFees,
    label: 'Unaffordable fees',
  },
  {
    key: UnenrolmentReason.AlreadyStudied,
    value: UnenrolmentReason.AlreadyStudied,
    label: 'Already studied / enrolled elsewhere',
  },
  {
    key: UnenrolmentReason.GeographicalInconvenience,
    value: UnenrolmentReason.GeographicalInconvenience,
    label: 'Geographical Inconvenience',
  },
  {
    key: UnenrolmentReason.Accident,
    value: UnenrolmentReason.Accident,
    label: 'Accidental enrolment / testing',
  },
  {
    key: UnenrolmentReason.Full,
    value: UnenrolmentReason.Full,
    label: 'Class is full',
  },
  {
    key: UnenrolmentReason.ClassCompletion,
    value: UnenrolmentReason.ClassCompletion,
    label: 'Class is finished / cancelled',
  },
  {
    key: UnenrolmentReason.Unknown,
    value: UnenrolmentReason.Unknown,
    label: 'Unknown',
  },
];

export const UNENROLMENT_QUIT_REASONS = [
  ...UNENROLMENT_SHARED_REASONS,
  {
    key: UnenrolmentReason.AdminDissatisfaction,
    value: UnenrolmentReason.AdminDissatisfaction,
    label: 'Admin dissatisfaction',
  },
  {
    key: UnenrolmentReason.ServiceDissatisfaction,
    value: UnenrolmentReason.ServiceDissatisfaction,
    label: 'Service dissatisfaction',
  },
  {
    key: UnenrolmentReason.NeverAttended,
    value: UnenrolmentReason.NeverAttended,
    label: 'Never attended',
  },
  {
    key: UnenrolmentReason.Graduation,
    value: UnenrolmentReason.Graduation,
    label: 'Graduation',
  },
  {
    key: UnenrolmentReason.DroppedCourse,
    value: UnenrolmentReason.DroppedCourse,
    label: 'Dropped course',
  },
  {
    key: UnenrolmentReason.TooEasy,
    value: UnenrolmentReason.TooEasy,
    label: 'Too easy / slow',
  },
  {
    key: UnenrolmentReason.TooHard,
    value: UnenrolmentReason.TooHard,
    label: 'Too hard / fast',
  },
  {
    key: UnenrolmentReason.NoImprovement,
    value: UnenrolmentReason.NoImprovement,
    label: 'No improvement',
  },
];

export const UNENROLMENT_MOVE_REASONS = [
  ...UNENROLMENT_SHARED_REASONS,
  {
    key: UnenrolmentReason.FriendPreference,
    value: UnenrolmentReason.FriendPreference,
    label: 'Be with school / friends',
  },
];

export const DEFAULT_UNENROLMENT_REASON = {
  key: UnenrolmentReason.Unknown,
  value: UnenrolmentReason.Unknown,
  label: 'Unknown',
};

export const EXAM_AUTODROP_SWITCH_KEY = 'examAutoDrop';
export const THEME_MODE_SWITCH_KEY = 'themeMode';
